import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import tzlookup from "tz-lookup";
import { addOpenMeteoForecastRequests } from "../../../../utils/openMeteoForecast";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const VISUAL_CROSSING_API_KEY = "3VKSWKB5Q6Q3AAFW6CKKPBX29";
const DATE_FORMAT = "YYYY-MM-DD";
const weatherApiStatus = {
  visualCrossingExpired: false,
  openMeteoExpired: false,
};

const ARCHIVE_CACHE_PREFIX = "openMeteoArchive";
const ARCHIVE_CACHE_INDEX_KEY = `${ARCHIVE_CACHE_PREFIX}:index`;
const ARCHIVE_CACHE_MAX_BYTES = 2 * 1024 * 1024;
const ARCHIVE_CACHE_MAX_KEYS = 20;
const ARCHIVE_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 30;

const canUseLocalStorage = () =>
  typeof window !== "undefined" && !!window.localStorage;

const normalizeArchiveIndex = (rawIndex) => {
  if (!Array.isArray(rawIndex)) return [];
  return rawIndex
    .map((entry) => {
      if (typeof entry === "string") {
        return { key: entry, size: 0, savedAt: 0 };
      }
      if (entry && typeof entry.key === "string") {
        return {
          key: entry.key,
          size: Number.isFinite(entry.size) ? entry.size : 0,
          savedAt: Number.isFinite(entry.savedAt) ? entry.savedAt : 0,
        };
      }
      return null;
    })
    .filter(Boolean);
};

const readArchiveIndex = () => {
  const rawIndex = window.localStorage.getItem(ARCHIVE_CACHE_INDEX_KEY);
  if (!rawIndex) return [];
  try {
    return normalizeArchiveIndex(JSON.parse(rawIndex));
  } catch {
    return [];
  }
};

const writeArchiveIndex = (index) => {
  window.localStorage.setItem(ARCHIVE_CACHE_INDEX_KEY, JSON.stringify(index));
};

const pruneArchiveIndex = (index, now) => {
  const next = [];
  const seen = new Set();

  index.forEach((entry) => {
    if (!entry?.key || seen.has(entry.key)) return;
    seen.add(entry.key);

    if (!entry.savedAt || now - entry.savedAt > ARCHIVE_CACHE_TTL_MS) {
      window.localStorage.removeItem(entry.key);
      return;
    }

    const raw = window.localStorage.getItem(entry.key);
    if (!raw) return;

    const size =
      Number.isFinite(entry.size) && entry.size > 0 ? entry.size : raw.length;
    next.push({ key: entry.key, size, savedAt: entry.savedAt });
  });

  return next;
};

const enforceArchiveLimits = (index) => {
  let totalSize = index.reduce(
    (sum, entry) => sum + (entry?.size || 0),
    0,
  );
  const next = [...index];

  while (
    next.length > ARCHIVE_CACHE_MAX_KEYS ||
    totalSize > ARCHIVE_CACHE_MAX_BYTES
  ) {
    const removed = next.pop();
    if (!removed) break;
    totalSize -= removed.size || 0;
    window.localStorage.removeItem(removed.key);
  }

  return next;
};

const readArchiveCache = (key) => {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data)) return null;
    const now = Date.now();
    if (
      Number.isFinite(parsed.savedAt) &&
      now - parsed.savedAt > ARCHIVE_CACHE_TTL_MS
    ) {
      window.localStorage.removeItem(key);
      const index = pruneArchiveIndex(
        readArchiveIndex().filter((entry) => entry.key !== key),
        now,
      );
      writeArchiveIndex(enforceArchiveLimits(index));
      return null;
    }
    const savedAt = Number.isFinite(parsed.savedAt) ? parsed.savedAt : now;
    const size = raw.length;
    const index = pruneArchiveIndex(
      readArchiveIndex().filter((entry) => entry.key !== key),
      now,
    );
    const next = enforceArchiveLimits([{ key, size, savedAt }, ...index]);
    writeArchiveIndex(next);
    return parsed.data;
  } catch {
    return null;
  }
};

const writeArchiveCache = (key, data) => {
  if (!canUseLocalStorage()) return;
  try {
    const savedAt = Date.now();
    const payload = JSON.stringify({ data, savedAt });
    const size = payload.length;
    if (size > ARCHIVE_CACHE_MAX_BYTES) {
      window.localStorage.removeItem(key);
      const index = pruneArchiveIndex(
        readArchiveIndex().filter((entry) => entry.key !== key),
        savedAt,
      );
      writeArchiveIndex(enforceArchiveLimits(index));
      return;
    }

    window.localStorage.setItem(key, payload);

    const index = pruneArchiveIndex(
      readArchiveIndex().filter((entry) => entry.key !== key),
      savedAt,
    );
    const next = enforceArchiveLimits([{ key, size, savedAt }, ...index]);
    writeArchiveIndex(next);
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

export const cleanGhName = (ghName) =>
  ghName.replace(/(?:\s\((?:S\d+|OLD)\))+$/, "").trimEnd();

export const filterVisiblePlotData = (plotData = {}) => {
  return Object.fromEntries(
    Object.entries(plotData || {}).map(([ghName, rows]) => [
      ghName,
      Object.fromEntries(
        Object.entries(rows || {}).map(([rowName, entries]) => [
          rowName,
          Array.isArray(entries)
            ? entries.filter((entry) => entry?.is_visible !== false)
            : entries,
        ]),
      ),
    ]),
  );
};

export const roundLeafSizeForDisplay = (value) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === "boolean" ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? Number(numericValue.toFixed(1))
    : null;
};

export const isDisplayedLeafSizeWithinRange = (value, min, max) => {
  const displayedValue = roundLeafSizeForDisplay(value);
  const minValue = Number(min);
  const maxValue = Number(max);

  return (
    displayedValue !== null &&
    Number.isFinite(minValue) &&
    Number.isFinite(maxValue) &&
    displayedValue >= minValue &&
    displayedValue <= maxValue
  );
};

const fetchFromVisualCrossing = async (latLong, startDate, endDate) => {
  if (!latLong) {
    return;
  }
  const apiUrl = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${latLong}/${startDate}/${endDate}?unitGroup=metric&include=days&key=${VISUAL_CROSSING_API_KEY}&contentType=json`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      if (response.status === 429) {
        console.error("Visual Crossing API limit reached.");
        weatherApiStatus.visualCrossingExpired = true;
      }
      return null;
    }

    const data = await response.json();
    return data.days.map((day) => ({
      date: dayjs(day.datetime).format(DATE_FORMAT),
      max: day.tempmax,
      min: day.tempmin,
    }));
  } catch (error) {
    console.error("Error fetching from Visual Crossing:", error);
    weatherApiStatus.visualCrossingExpired = true;
    return null;
  }
};

// Function to fetch from Open-Meteo API (no key required)
const fetchFromOpenMeteo = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
  { failOnPartialFailure = false } = {},
) => {
  // Initialize array to hold all temperature data
  const allTemperatureData = [];

  try {
    // 1. **Validate and Parse Inputs**

    // Validate and parse latLong
    const [latStr, lonStr] = latLong.split(",").map((coord) => coord.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (
      isNaN(lat) ||
      isNaN(lon) ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      throw new Error(
        'Invalid latLong format. Expected format "lat,lon" with valid numerical values.',
      );
    }

    // Parse and validate input dates
    const inputStartDate = dayjs(startDate, DATE_FORMAT, true);
    const inputEndDate = dayjs(endDate, DATE_FORMAT, true);
    if (!inputStartDate.isValid() || !inputEndDate.isValid()) {
      throw new Error('Invalid date format. Expected format "YYYY-MM-DD".');
    }

    if (inputEndDate.isBefore(inputStartDate)) {
      throw new Error("endDate cannot be earlier than startDate.");
    }

    // 2. **Define Date Boundaries**

    const today = getTodayDate(latLong);
    if (!today || !today.isValid()) {
      throw new Error("Failed to retrieve today's date.");
    }

    // Define valid date ranges for each API
    const historicalStartMin = dayjs("1940-01-01");
    const historicalEndMax = today.subtract(3, "day"); // today - 3 days

    const forecastStartMin = historicalEndMax.add(1, "day"); // today - 2 days
    const forecastEndMax = today.add(14, "day"); // today + 14 days

    // 3. **Clamp Input Dates to Valid Ranges**

    const clampedStartDate = inputStartDate.isBefore(historicalStartMin)
      ? historicalStartMin
      : inputStartDate;

    const clampedEndDate = inputEndDate.isAfter(forecastEndMax)
      ? forecastEndMax
      : inputEndDate;

    // 4. **Determine Required Data Fetches**

    const needsHistorical = clampedStartDate.isSameOrBefore(historicalEndMax);
    const needsForecast = clampedEndDate.isSameOrAfter(forecastStartMin);

    if (!needsHistorical && !needsForecast) {
      console.warn(
        "Requested date range does not fall within the supported API ranges.",
      );
      return allTemperatureData;
    }

    // 5. **Get Timezone**
    const timezone = getTimezone(latLong);
    if (!timezone) {
      throw new Error("Failed to retrieve timezone.");
    }

    // 6. **Prepare Fetch URLs**

    const fetchPromises = [];

    // Historical API Fetch
    if (needsHistorical) {
      const histStart = clampedStartDate.isBefore(historicalStartMin)
        ? historicalStartMin
        : clampedStartDate;
      const histEnd = clampedEndDate.isBefore(historicalEndMax)
        ? clampedEndDate
        : historicalEndMax;

      if (!histStart.isAfter(histEnd)) {
        const histStartStr = histStart.format(DATE_FORMAT);
        const histEndStr = histEnd.format(DATE_FORMAT);
        const cacheKey = `${ARCHIVE_CACHE_PREFIX}:${lat},${lon}:${histStartStr}:${histEndStr}:${timezone}`;
        const cached = readArchiveCache(cacheKey);

        if (cached) {
          fetchPromises.push(Promise.resolve(cached));
        } else {
          const historicalUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${histStartStr}&end_date=${histEndStr}&daily=temperature_2m_max,temperature_2m_min&timezone=${encodeURIComponent(timezone)}`;
          fetchPromises.push(
            fetch(historicalUrl)
              .then((response) => {
                if (!response.ok) {
                  throw new Error(
                    `Historical API responded with status ${response.status}`,
                  );
                }
                return response.json();
              })
              .then((data) => {
                if (!data.daily || !data.daily.time) {
                  throw new Error(
                    "Invalid historical data format received from API.",
                  );
                }
                const mapped = data.daily.time.map((date, index) => ({
                  date: dayjs(date).format(DATE_FORMAT),
                  max: data.daily.temperature_2m_max[index],
                  min: data.daily.temperature_2m_min[index],
                }));
                writeArchiveCache(cacheKey, mapped);
                return mapped;
              }),
          );
        }
      }
    }

    // Forecast API Fetch
    if (needsForecast) {
      const foreStart = clampedStartDate.isAfter(forecastStartMin)
        ? clampedStartDate
        : forecastStartMin;
      const foreEnd = clampedEndDate.isBefore(forecastEndMax)
        ? clampedEndDate
        : forecastEndMax;

      if (!foreStart.isAfter(foreEnd)) {
        addOpenMeteoForecastRequests({
          fetchPromises,
          lat,
          lon,
          forecastStart: foreStart,
          forecastEnd: foreEnd,
          timezone,
          siteMetadata,
          dateFormat: DATE_FORMAT,
        });
      }
    }

    // 7. **Execute Fetches Concurrently**

    const results = await Promise.allSettled(fetchPromises);

    const failedResults = [];
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        allTemperatureData.push(...result.value);
      } else {
        console.error("Error fetching temperature data:", result.reason);
        failedResults.push(result.reason);
        // Optionally, handle specific errors or set a status flag here
      }
    });

    if (failedResults.length && failOnPartialFailure) {
      const reason = failedResults
        .map((error) => error?.message || String(error))
        .filter(Boolean)
        .join("; ");
      throw new Error(reason || "Open-Meteo temperature request failed.");
    }

    return allTemperatureData;
  } catch (error) {
    console.error("Unexpected error in fetchFromOpenMeteo:", error);
    if (failOnPartialFailure) {
      throw error;
    }
    // Optionally, handle or propagate the error as needed
    return null;
  }
};

export const getTimezone = (latLong) => {
  if (!latLong) return null;
  const [lat, lon] = latLong.split(",").map(Number);
  return tzlookup(lat, lon);
};

export const fetchTemperatureData = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) => {
  if (!latLong) {
    return null;
  }
  try {
    // Try Open-Meteo if not expired
    if (!weatherApiStatus.openMeteoExpired) {
      // console.log("Trying Open Meteo...");
      const openMeteoData = await fetchFromOpenMeteo(
        latLong,
        startDate,
        endDate,
        siteMetadata,
      );
      if (Array.isArray(openMeteoData) && openMeteoData.length) {
        return openMeteoData;
      }
    }

    // If Open-Meteo failed or expired, try Visual Crossing
    if (!weatherApiStatus.visualCrossingExpired) {
      console.log("Trying Visual Crossing...");
      const visualCrossingData = await fetchFromVisualCrossing(
        latLong,
        startDate,
        endDate,
      );
      if (visualCrossingData) {
        return visualCrossingData;
      }
    }

    console.error("All APIs have expired or failed.");
    return null;
  } catch (error) {
    console.error("Error fetching temperature data:", error);
    return null;
  }
};

export const fetchOpenMeteoTemperatureData = (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) =>
  fetchFromOpenMeteo(latLong, startDate, endDate, siteMetadata, {
    failOnPartialFailure: true,
  });

export const getTodayDate = (latLong) => {
  if (!latLong) return dayjs();
  const timeZone = getTimezone(latLong);
  return dayjs().tz(timeZone);
};

export const calculateGDD = (temperatureData, baseTemperature = 5) => {
  let totalGDD = 0;
  if (temperatureData) {
    temperatureData.forEach(({ max, min }) => {
      const dailyGDD = (max + min) / 2 - baseTemperature;
      totalGDD += dailyGDD > 0 ? dailyGDD : 0;
    });
  }
  return totalGDD;
};

/**
 * Calculate distance between two geographic points using Haversine formula
 * @param {number} lat1 - Latitude of first point in degrees
 * @param {number} lon1 - Longitude of first point in degrees
 * @param {number} lat2 - Latitude of second point in degrees
 * @param {number} lon2 - Longitude of second point in degrees
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371000; // Earth radius in meters
  const lat1InRadians = (lat1 * Math.PI) / 180;
  const lat2InRadians = (lat2 * Math.PI) / 180;
  const latDifferenceInRadians = ((lat2 - lat1) * Math.PI) / 180;
  const lonDifferenceInRadians = ((lon2 - lon1) * Math.PI) / 180;

  const haversineSquared =
    Math.sin(latDifferenceInRadians / 2) *
      Math.sin(latDifferenceInRadians / 2) +
    Math.cos(lat1InRadians) *
      Math.cos(lat2InRadians) *
      Math.sin(lonDifferenceInRadians / 2) *
      Math.sin(lonDifferenceInRadians / 2);
  const angularDistance =
    2 *
    Math.atan2(Math.sqrt(haversineSquared), Math.sqrt(1 - haversineSquared));

  return earthRadius * angularDistance; // in meters
};

/**
 * Calculate row length using coordinates
 * @param {Array<Array<number>>} coordinates - Array of [lat, lng] coordinate pairs
 * @returns {number} Length in meters
 */
export const calculateRowDimensions = (coordinates) => {
  if (!coordinates || coordinates.length < 2) return { length: 0, width: 0 };
  // Get the longer dimension (length) of the rectangular field
  // Assuming coordinates form a roughly rectangular shape
  const latitudes = coordinates.map((coord) => coord[0]);
  const longitudes = coordinates.map((coord) => coord[1]);

  const latDiff = Math.max(...latitudes) - Math.min(...latitudes);
  const lonDiff = Math.max(...longitudes) - Math.min(...longitudes);

  // Convert lat/lon differences to actual distances
  const avgLat = (Math.max(...latitudes) + Math.min(...latitudes)) / 2;
  const latDistance = calculateDistance(avgLat, 0, avgLat + latDiff, 0);
  const lonDistance = calculateDistance(
    Math.min(...latitudes),
    0,
    Math.min(...latitudes),
    lonDiff,
  );

  return latDistance > lonDistance
    ? { length: latDistance, width: lonDistance }
    : { length: lonDistance, width: latDistance };
};

/**
 * Calculate row length using coordinates
 * @param {Array<Array<number>>} coordinates - Array of [lat, lng] coordinate pairs
 * @returns {number} Length in meters
 */
export const calculateRowLength = (coordinates) => {
  return calculateRowDimensions(coordinates)?.length || 0;
};

// Helper function to convert hex to rgba
export const hexToRgba = (hex, opacity = 1) => {
  // Use a direct rgba value as a fallback
  const fallbackRgba = `rgba(24, 144, 255, ${opacity})`;

  try {
    // Guard against null or undefined
    if (!hex) return fallbackRgba;

    // Remove the hash if it exists
    hex = hex.replace("#", "");

    // Handle both 3-digit and 6-digit hex
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    // Validate it's a proper hex
    if (!/^[0-9A-F]{6}$/i.test(hex)) {
      return fallbackRgba;
    }

    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Return rgba value
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch (error) {
    console.error("Error converting hex to rgba:", error);
    return fallbackRgba;
  }
};
